import { supabase } from '@/core/database/supabase';
import type { 
  AutomationRule, 
  AutomationLog, 
  AutomationStats, 
  AutomationFilters 
} from '../types/automation.types';

export const AutomationService = {
  /**
   * Danh sách các quy tắc tự động hóa
   */
  async getRules(companyId: string, filters: AutomationFilters = {}): Promise<{ data: AutomationRule[], total: number }> {
    let query = supabase
      .from('automation_rules')
      .select('*', { count: 'exact' })
      .eq('company_id', companyId);

    if (filters.status && filters.status !== 'all') {
      query = query.eq('is_active', filters.status === 'active');
    }
    if (filters.type && filters.type !== 'all') {
      query = query.eq('trigger_type', filters.type);
    }
    if (filters.search) {
      query = query.ilike('name', `%${filters.search}%`);
    }

    query = query.order('created_at', { ascending: false });

    const { data, error, count } = await query;

    if (error) throw error;
    return {
      data: (data || []) as AutomationRule[],
      total: count || 0
    };
  },

  /**
   * Lịch sử thực thi (Logs)
   */
  async getLogs(companyId: string, ruleId?: string): Promise<AutomationLog[]> {
    let query = supabase
      .from('automation_logs')
      .select('*')
      .eq('company_id', companyId);

    if (ruleId) query = query.eq('rule_id', ruleId);

    query = query.order('executed_at', { ascending: false }).limit(50);

    const { data, error } = await query;
    if (error) throw error;
    return data as AutomationLog[];
  },

  /**
   * Thống kê vận hành tự động
   */
  async getAutomationStats(companyId: string): Promise<AutomationStats> {
    const { data: rules } = await supabase.from('automation_rules').select('is_active').eq('company_id', companyId);
    const { data: logs } = await supabase.from('automation_logs').select('status').eq('company_id', companyId);

    const activeRules = rules?.filter(r => r.is_active).length || 0;
    const executionsToday = logs?.length || 0;
    const successRate = logs && logs.length > 0 
      ? (logs.filter(l => l.status === 'success').length / logs.length) * 100 
      : 100;

    return {
      activeRules,
      executionsToday,
      successRate,
      criticalAlerts: logs?.filter(l => l.status === 'failure').length || 0
    };
  },

  async createRule(data: Partial<AutomationRule>): Promise<AutomationRule> {
    const { data: created, error } = await supabase
      .from('automation_rules')
      .insert(data)
      .select()
      .single();

    if (error) throw error;
    return created as AutomationRule;
  },

  async updateRule(id: string, updates: Partial<AutomationRule>): Promise<AutomationRule> {
    const { data, error } = await supabase
      .from('automation_rules')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as AutomationRule;
  },

  /**
   * Bật/Tắt quy tắc
   */
  async toggleRule(id: string, isActive: boolean): Promise<void> {
    const { error } = await supabase
      .from('automation_rules')
      .update({ is_active: isActive })
      .eq('id', id);

    if (error) throw error;
  },

  /**
   * Thực thi thủ công (Run now)
   */
  async runRuleNow(id: string): Promise<void> {
    // Trong thực tế, đây sẽ gọi một Edge Function xử lý engine automation
    const { error } = await supabase.functions.invoke('execute-automation-rule', {
      body: { ruleId: id }
    });
    
    // Nếu chưa có edge function, mock log để UI hiển thị
    if (error) {
      console.warn('Edge function not found, simulation mode');
    }
  }
};
